import React from "react";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import SavedPoint from "./SavedPoint";
import SavedLine from "./SavedLine";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faSearchLocation,
  faTimes,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  ApplicationMessage,
  messageState,
} from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";
import { geometryType } from "./AddCoordinateToolBar";
import coordinateListState from "./coordinateListState";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import { downloadFile } from "../data/misc";

import "./coordinateList.css";

const pointHeader = "RoadNo,RoadName,Latitude,Longitude,SLK,Note,Geometry";
const segmentHeader =
  "Start RoadNo,Start RoadName,Start SLK,Start Latitude,Start Longitude,End RoadNo,End RoadName,End SLK,End Latitude,End Longitude,Note,Distance,Geometry";

/* modified from https://gist.github.com/Jezternz/c8e9fafc2c114e079829974e3764db75 in response to https://stackoverflow.com/questions/1293147/javascript-code-to-parse-csv-data */
function csvStringToArray(data) {
  const re = /(,|\r?\n|\r|^)(?:"([^"]*(?:""[^"]*)*)"|([^,\r\n]*))/gi;
  const result = [];
  let matches;
  while ((matches = re.exec(data))) {
    result.push(
      matches[2] !== undefined ? matches[2].replace(/""/g, '"') : matches[3]
    );
  }
  return result;
}

async function parsePoint(line) {
  const data = csvStringToArray(line);
  const { Point } = await requestEsriModules({
    Point: "esri/geometry/Point",
  });
  return {
    roadNo: data[0],
    roadName: data[1],
    slk: data[4],
    latitude: data[2],
    longitude: data[3],
    note: data[5],
    type: geometryType.POINT,
    geometry: Point.fromJSON(JSON.parse(data[6])),
  };
}

async function parseSegment(line) {
  const data = csvStringToArray(line);
  const { GeometryEngine, Polyline } = await requestEsriModules({
    GeometryEngine: "esri/geometry/geometryEngineAsync",
    Polyline: "esri/geometry/Polyline",
  });
  const geometry = Polyline.fromJSON(JSON.parse(data[12]));
  return {
    startRoadNo: data[0],
    startRoadName: data[1],
    startSLK: data[2],
    startLatitude: data[3],
    startLongitude: data[4],
    endRoadNo: data[5],
    endRoadName: data[6],
    endSLK: data[7],
    endLatitude: data[8],
    endLongitude: data[9],
    note: data[10],
    type: geometryType.POLYLINE,
    geometry,
    alongRoadDistance: await GeometryEngine.geodesicLength(geometry, "meters"),
  };
}

export default class CoordinateList extends React.Component {
  constructor(props) {
    super(props);
    this.state = { savedCoordinates: [] };

    this.zoomToGeometry = this.zoomToGeometry.bind(this);
    this.removeCoordinate = this.removeCoordinate.bind(this);
    this.handleNoteChange = this.handleNoteChange.bind(this);
    this.handleTextExport = this.handleExport.bind(this, false);
    this.handleCSVExport = this.handleExport.bind(this, true);
    this.handleSelectCSVFile = this.handleSelectCSVFile.bind(this);
    this.handleCSVImport = this.handleCSVImport.bind(this);

    this.fileInput = React.createRef();
  }

  componentDidMount() {
    coordinateListState.subscribe("savedCoordinates", (savedCoordinates) => {
      this.setState({ savedCoordinates });
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.playerContext !== prevProps.playerContext) {
      this.props.playerContext.onNewPlaylist(
        () => (coordinateListState.savedCoordinates = [])
      );
    }
  }

  handleNoteChange(e) {
    const previousCoords = [...coordinateListState.savedCoordinates];
    previousCoords[e.target.dataset.index].note = e.target.value;
    coordinateListState.savedCoordinates = previousCoords;
  }

  //zooms to a given geometry and flashes it on the map
  zoomToGeometry(geometry) {
    if (!geometry) {
      messageState.message = ApplicationMessage.Error(
        "Cannot zoom, no data for this location."
      );
    }
    if (this.props.playerContext.playing)
      this.props.playerContext.requestPause();
    setTimeout(() => {
      mapHelper
        .zoomToGeometry(geometry)
        .then(() => mapHelper.flashGeoOutline(geometry));
    }, 250); //using setTimeout to stop zoom from flicking back to car
  }

  handleSelectCSVFile() {
    this.fileInput.current.value = null;
    this.fileInput.current.click();
  }

  handleCSVImport(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const data = reader.result.split(/\r?\n/);

      let currentType = null;
      let lineNumber = 0;
      let result = [];
      for (const line of data) {
        lineNumber++;
        try {
          if (line === "") continue;
          // need to use startsWith since it has less columns than segmentHeader which will be populated with extra ,'s on save in excel
          if (line.startsWith(pointHeader)) currentType = geometryType.POINT;
          else if (line === segmentHeader) currentType = geometryType.POLYLINE;
          else if (currentType !== null) {
            switch (currentType) {
              case geometryType.POINT:
                result.push(await parsePoint(line));
                break;
              case geometryType.POLYLINE:
                result.push(await parseSegment(line));
                break;
              default:
                throw new Error("Unexpected coordinate type");
            }
          } else {
            throw new Error("Current coordinate cannot be determined");
          }
        } catch {
          messageState.message = ApplicationMessage.Error(
            `Cannot read CSV file, line ${lineNumber}`
          );
          return;
        }
      }

      coordinateListState.savedCoordinates = [
        ...this.state.savedCoordinates,
        ...result,
      ];
    };

    reader.readAsText(file);
  }

  //exports all saved coordinates into a .txt or .csv file
  handleExport(toCsv = false) {
    function createHeaderRow(type) {
      switch (type) {
        case geometryType.POINT:
          return pointHeader;
        case geometryType.POLYLINE:
          return segmentHeader;
        default:
          throw new Error("Unexpected coordinate type");
      }
    }
    function createDataRow(
      coord,
      includeLabel = false,
      separator = ",",
      includeGeometry = true
    ) {
      switch (coord.type) {
        case geometryType.POINT:
          return `${includeLabel ? "RoadNo:" : ""}${coord.roadNo}${separator}${
            includeLabel ? "RoadName:" : ""
          }${coord.roadName}${separator}${includeLabel ? "Latitude:" : ""}${
            coord.latitude
          }${separator}${includeLabel ? "Longitude:" : ""}${
            coord.longitude
          }${separator}${includeLabel ? "SLK:" : ""}${coord.slk}${separator}${
            includeLabel ? "Note:" : ""
          }${coord.note}${
            includeGeometry &&
            `${separator}"${JSON.stringify(coord.geometry.toJSON()).replaceAll(
              '"',
              '""'
            )}"`
          }`;
        case geometryType.POLYLINE:
          return `${includeLabel ? "Start RoadNo:" : ""}${
            coord.startRoadNo
          }${separator}${includeLabel ? "Start RoadName:" : ""}${
            coord.startRoadName
          }${separator}${includeLabel ? "Start SLK:" : ""}${
            coord.startSLK
          }${separator}${includeLabel ? "Start Latitude:" : ""}${
            coord.startLatitude
          }${separator}${includeLabel ? "Start Longitude:" : ""}${
            coord.startLongitude
          }${separator}${includeLabel ? "End RoadNo:" : ""}${
            coord.endRoadNo
          }${separator}${includeLabel ? "End RoadName:" : ""}${
            coord.endRoadName
          }${separator}${includeLabel ? "End SLK:" : ""}${
            coord.endSLK
          }${separator}${includeLabel ? "End Latitude:" : ""}${
            coord.endLatitude
          }${separator}${includeLabel ? "End Longitude:" : ""}${
            coord.endLongitude
          }${separator}${includeLabel ? "Note:" : ""}${coord.note}${separator}${
            includeLabel ? "Distance:" : ""
          }${coord.alongRoadDistance.toFixed(2)}m${
            includeGeometry &&
            `${separator}"${JSON.stringify(coord.geometry.toJSON()).replaceAll(
              '"',
              '""'
            )}"`
          }`;
        default:
          throw new Error("Unexpected coordinate type");
      }
    }
    const { result: coordinateData } = this.state.savedCoordinates.reduce(
      ({ lastCoordType, result }, coord) => {
        if (toCsv && lastCoordType !== coord.type) {
          result.push(createHeaderRow(coord.type));
        }
        if (toCsv) result.push(createDataRow(coord));
        else result.push(createDataRow(coord, true, " ", false));
        return { lastCoordType: coord.type, result };
      },
      { lastCoordType: null, result: [] }
    );

    const file = new Blob([coordinateData.join("\n")], {
      type: "text/plain",
      endings: "native",
    });
    downloadFile(
      URL.createObjectURL(file),
      `savedCoordinates.${toCsv ? "csv" : "txt"}`
    );
  }

  removeCoordinate(index) {
    const newState = coordinateListState.savedCoordinates;
    newState.splice(index, 1);
    coordinateListState.savedCoordinates = newState;
  }

  render() {
    const importButton = (
      <>
        <button
          className="light"
          title="Import from CSV"
          type="button"
          onClick={this.handleSelectCSVFile}
        >
          Import from CSV <FontAwesomeIcon icon={faUpload} />
        </button>
        <input
          type="file"
          accept=".csv"
          onChange={this.handleCSVImport}
          style={{ display: "none" }}
          ref={this.fileInput}
        ></input>
      </>
    );

    return (
      <div className="coordinatesList">
        {this.state.savedCoordinates.length > 0 ? (
          <>
            <div className="export">
              {/* Commenting this out rather than removing, since it may be desired again in future. 
              If it is used the the CSV export should use faFileExcel from the @fortawesome/free-regular-svg-icons library
              <button
                className="light"
                title="Export to Text"
                type="button"
                onClick={this.handleTextExport}
              >
                Export to Text <FontAwesomeIcon icon={faFileAlt} />
              </button> */}
              {importButton}
              <button
                className="light"
                title="Export to CSV"
                type="button"
                onClick={this.handleCSVExport}
              >
                Export to CSV <FontAwesomeIcon icon={faDownload} />
              </button>
            </div>
            {this.state.savedCoordinates.map((coord, i) => (
              <div className="coordinateRow" key={i}>
                <button
                  title="Zoom"
                  type="button"
                  className="button light"
                  onClick={() => this.zoomToGeometry(coord.geometry)}
                >
                  <FontAwesomeIcon icon={faSearchLocation}></FontAwesomeIcon>
                </button>
                <SavedPoint point={coord} />
                <SavedLine
                  line={coord}
                  isSameRoad={coord.startRoadName === coord.endRoadName}
                  hasSlk={coord.startSLK && coord.endSLK}
                />
                <input
                  type="text"
                  data-index={i}
                  className="note"
                  value={coord.note}
                  onChange={this.handleNoteChange}
                ></input>
                <button
                  title="Remove"
                  type="button"
                  className="button"
                  onClick={() => this.removeCoordinate(i)}
                >
                  <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                </button>
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="export">{importButton}</div>
            <div>Coordinate(s) not yet added</div>
          </>
        )}
      </div>
    );
  }
}
